/**
 * Axios API Client with Automatic Token Refresh
 *
 * 문서 참고: coin-server/docs/frontend/REFRESH_TOKEN_MIGRATION_GUIDE.md
 *
 * Before (Legacy):
 * - fetch API 직접 사용
 * - 수동 토큰 갱신
 *
 * After (Standard):
 * - Axios interceptor로 자동 갱신
 * - 401 에러 시 자동으로 Refresh Token 사용
 */

import axios, {type AxiosInstance} from 'axios';
import { NEW_API_URL } from '../app/constants/APIKEYS';

// 디버그 플래그: 자동 로그아웃 비활성화 (환경변수로 제어)
const DISABLE_AUTO_LOGOUT = import.meta.env.VITE_DISABLE_AUTO_LOGOUT === 'true';

// 디버그 모드 경고
if (DISABLE_AUTO_LOGOUT) {
  console.warn(
    '%c[DEBUG MODE] Auto-logout is DISABLED!',
    'background: #ff6b6b; color: white; font-weight: bold; padding: 4px 8px; border-radius: 4px;',
    '\n⚠️ Authentication errors will NOT trigger logout.',
    '\n⚠️ Only use this in development for debugging.',
    '\n⚠️ Set VITE_DISABLE_AUTO_LOGOUT=false in production!'
  );
}

// ⚠️ CRITICAL: Refresh Token 동시성 제어
// 여러 API 요청이 동시에 401을 받아도 refresh는 한 번만 호출
let isRefreshing = false;
let failedQueue: Array<{
  resolve: (value: any) => void;
  reject: (reason?: any) => void;
}> = [];

// 🆕 BroadcastChannel for multi-tab token refresh coordination
// 여러 탭이 열려있을 때 토큰 갱신을 조율하여 TOKEN_REUSE 방지
const refreshChannel = new BroadcastChannel('token-refresh');
let globalRefreshPromise: Promise<string> | null = null;

// 다른 탭에서 토큰 갱신 완료 메시지 수신
refreshChannel.onmessage = (event) => {
  if (event.data.type === 'REFRESH_COMPLETE') {
    console.log('[BroadcastChannel] 📡 Received token refresh from another tab');

    // localStorage에서 새 토큰 읽기 (다른 탭이 이미 저장함)
    const storageData = localStorage.getItem('token-storage');
    if (storageData) {
      try {
        const { state } = JSON.parse(storageData);
        const newAccessToken = state?.accessToken;

        if (newAccessToken) {
          // 대기 중인 요청들에게 새 토큰 전달
          processQueue(null, newAccessToken);
          isRefreshing = false;
          globalRefreshPromise = null;

          console.log('[BroadcastChannel] ✅ Queue processed with token from another tab');
        }
      } catch (e) {
        console.error('[BroadcastChannel] Failed to read token from storage:', e);
      }
    }
  } else if (event.data.type === 'REFRESH_FAILED') {
    console.log('[BroadcastChannel] ❌ Token refresh failed in another tab');
    processQueue(new Error('Token refresh failed in another tab'), null);
    isRefreshing = false;
    globalRefreshPromise = null;
  }
};

/**
 * Refresh가 완료되면 대기 중인 모든 요청을 재시도
 */
const processQueue = (error: any = null, token: string | null = null) => {
  failedQueue.forEach((prom) => {
    if (error) {
      prom.reject(error);
    } else {
      prom.resolve(token);
    }
  });

  failedQueue = [];
};

// Axios 인스턴스 생성
// ✅ NEW: VITE_APP_NEW_API_URL 사용 (새로운 JWT 인증 시스템 전용)
// 예: NEW_API_URL = "https://api.bynova.net/v1"
// 요청 시: apiClient.post('/auth/login') → https://api.bynova.net/v1/auth/login
const apiClient: AxiosInstance = axios.create({
  baseURL: NEW_API_URL,
  headers: {
    'Content-Type': 'application/json',
  },
});

// 요청 인터셉터: Access Token 자동 추가
apiClient.interceptors.request.use(
  (config) => {
    // localStorage에서 토큰 직접 읽기 (Zustand store 순환 참조 방지)
    const storageData = localStorage.getItem('token-storage');
    if (storageData) {
      try {
        const { state } = JSON.parse(storageData);
        const accessToken = state?.accessToken;

        if (accessToken) {
          config.headers.Authorization = `Bearer ${accessToken}`;
        }
      } catch (e) {
        console.error('[Axios] Failed to read access token:', e);
      }
    }
    return config;
  },
  (error) => Promise.reject(error)
);

// 응답 인터셉터: 401 에러 또는 EXPIRED_TOKEN 응답 시 Refresh Token으로 갱신
apiClient.interceptors.response.use(
  (response) => {
    // 200 OK지만 응답 본문에 EXPIRED_TOKEN 코드가 있는 경우도 처리
    // 예: { "success": false, "code": "EXPIRED_TOKEN", "message": "Token expired" }
    const isExpiredToken =
      response.data?.code === 'EXPIRED_TOKEN' ||
      response.data?.error?.code === 'EXPIRED_TOKEN';

    if (isExpiredToken && !(response.config as any)._retry) {
      console.log('[Refresh] 🔄 Detected EXPIRED_TOKEN in response body (200 OK)');
      // Promise를 reject하여 error 핸들러로 보냄
      return Promise.reject({
        config: response.config,
        response: response,
        isExpiredTokenInBody: true,
      });
    }

    return response;
  },
  async (error) => {
    const originalRequest = error.config as any;
    const isExpiredInBody = error.isExpiredTokenInBody;

    // ⚠️ IMPORTANT: 인증 엔드포인트는 refresh 시도하지 않음
    const isAuthEndpoint =
      originalRequest.url?.includes('/auth/login') ||
      originalRequest.url?.includes('/auth/register') ||
      originalRequest.url?.includes('/auth/refresh');

    // 401 에러이거나, 응답 본문에 EXPIRED_TOKEN이 있는 경우
    const shouldRefresh =
      (error.response?.status === 401 || isExpiredInBody) &&
      !originalRequest?._retry &&
      !isAuthEndpoint; // 인증 엔드포인트는 제외

    if (shouldRefresh) {
      originalRequest._retry = true;

      // ⚠️ CRITICAL: 이미 refresh 진행 중이면 대기열에 추가
      if (isRefreshing) {
        console.log('[Refresh] ⏳ Already refreshing, queueing request:', {
          url: originalRequest.url,
          method: originalRequest.method,
          queueSize: failedQueue.length,
        });

        // Promise를 생성하여 대기열에 추가
        return new Promise((resolve, reject) => {
          failedQueue.push({
            resolve: (token: string) => {
              // 새 토큰으로 헤더 업데이트 후 재시도
              originalRequest.headers.Authorization = `Bearer ${token}`;
              resolve(apiClient(originalRequest));
            },
            reject: (err: any) => {
              reject(err);
            },
          });
        });
      }

      // 🆕 Check if another tab is already refreshing
      // BroadcastChannel을 통해 다른 탭의 갱신을 기다림
      if (globalRefreshPromise) {
        console.log('[Refresh] ⏳ Waiting for token refresh from another tab...');
        try {
          const newToken = await globalRefreshPromise;
          originalRequest.headers.Authorization = `Bearer ${newToken}`;
          return apiClient(originalRequest);
        } catch (err) {
          console.error('[Refresh] ❌ Token refresh from another tab failed:', err);
          throw err;
        }
      }

      console.log('[Refresh] 🔄 Starting token refresh flow...', {
        url: originalRequest.url,
        method: originalRequest.method,
        trigger: error.response?.status === 401 ? '401 status' : 'EXPIRED_TOKEN in body',
      });

      isRefreshing = true;

      // 🆕 Create global promise for other tabs to wait on
      globalRefreshPromise = new Promise<string>((resolve, reject) => {
        // This promise will be resolved/rejected when BroadcastChannel receives message
        const originalResolve = resolve;
        const originalReject = reject;

        // Override to also handle local resolution
        (window as any)._globalRefreshResolve = originalResolve;
        (window as any)._globalRefreshReject = originalReject;
      });

      try {
        // localStorage에서 Refresh Token 읽기
        const storageData = localStorage.getItem('token-storage');
        if (!storageData) {
          console.error('[Refresh] ❌ No token storage found in localStorage');
          throw new Error('No token storage found');
        }

        const { state } = JSON.parse(storageData);
        const refreshToken = state?.refreshToken;

        console.log('[Refresh] 📦 Token storage data:', {
          hasAccessToken: !!state?.accessToken,
          hasRefreshToken: !!refreshToken,
          expiresIn: state?.expiresIn,
        });

        if (!refreshToken) {
          console.error('[Refresh] ❌ No refresh token found in storage');
          throw new Error('No refresh token found');
        }

        // Refresh Token으로 새 토큰 발급
        // ✅ NEW_API_URL 사용 (새로운 JWT 인증 시스템 전용)
        console.log('[Refresh] 📡 Sending refresh request to:', `${NEW_API_URL}/auth/refresh`);

        const response = await axios.post(`${NEW_API_URL}/auth/refresh`, {
          refreshToken,
        });

        console.log('[Refresh] 📥 Server response:', {
          status: response.status,
          hasData: !!response.data,
          dataStructure: response.data,
        });

        // 응답 데이터 구조 확인 및 토큰 추출
        let accessToken, newRefreshToken, expiresIn;

        if (response.data?.data) {
          // 예상 구조: { data: { accessToken, refreshToken, expiresIn } }
          accessToken = response.data.data.accessToken;
          newRefreshToken = response.data.data.refreshToken;
          expiresIn = response.data.data.expiresIn;
        } else if (response.data?.accessToken) {
          // 대안 구조: { accessToken, refreshToken, expiresIn }
          accessToken = response.data.accessToken;
          newRefreshToken = response.data.refreshToken;
          expiresIn = response.data.expiresIn;
        } else {
          console.error('[Refresh] ❌ Unexpected response structure:', response.data);
          throw new Error('Invalid refresh token response structure');
        }

        if (!accessToken || !newRefreshToken) {
          console.error('[Refresh] ❌ Missing tokens in response:', {
            hasAccessToken: !!accessToken,
            hasRefreshToken: !!newRefreshToken,
          });
          throw new Error('Missing tokens in refresh response');
        }

        console.log('[Refresh] ✅ New tokens received:', {
          hasNewAccessToken: !!accessToken,
          hasNewRefreshToken: !!newRefreshToken,
          newExpiresIn: expiresIn,
        });

        // 새 토큰 저장 (localStorage에 직접 저장)
        // ⚠️ Zustand persist 구조 맞춤: { state: {...}, version: 0 }
        const newState = {
          ...state,
          accessToken,
          refreshToken: newRefreshToken,
          expiresIn: expiresIn || 900,
        };
        localStorage.setItem(
          'token-storage',
          JSON.stringify({ state: newState, version: 0 })
        );

        console.log('[Refresh] 💾 Tokens saved to localStorage');

        // Zustand 스토어도 강제 업데이트 (storage 이벤트 트리거)
        // localStorage.setItem만으로는 같은 탭에서 Zustand가 업데이트 안 될 수 있음
        window.dispatchEvent(new StorageEvent('storage', {
          key: 'token-storage',
          newValue: JSON.stringify({ state: newState, version: 0 }),
          url: window.location.href,
        }));

        console.log('[Refresh] 📢 Storage event dispatched for Zustand sync');

        // 🆕 Notify other tabs via BroadcastChannel
        refreshChannel.postMessage({
          type: 'REFRESH_COMPLETE',
          token: accessToken,
          timestamp: Date.now(),
        });
        console.log('[BroadcastChannel] 📡 Notified other tabs of successful refresh');

        // 대기 중인 모든 요청에 새 토큰 전달
        processQueue(null, accessToken);

        // 실패한 요청 재시도
        originalRequest.headers.Authorization = `Bearer ${accessToken}`;

        console.log('[Refresh] 🔁 Retrying original request:', {
          url: originalRequest.url,
          method: originalRequest.method,
        });

        return apiClient(originalRequest);
      } catch (refreshError) {
        // 대기 중인 모든 요청에 에러 전달
        processQueue(refreshError, null);

        // 🆕 Notify other tabs of refresh failure
        refreshChannel.postMessage({
          type: 'REFRESH_FAILED',
          error: refreshError instanceof Error ? refreshError.message : String(refreshError),
          timestamp: Date.now(),
        });
        console.log('[BroadcastChannel] 📡 Notified other tabs of refresh failure');

        // Refresh 실패 → 로그아웃 처리
        console.error('[Axios] Refresh token failed:', refreshError);

        if (DISABLE_AUTO_LOGOUT) {
          console.error(
            '%c[DEBUG] Auto-logout SKIPPED (refresh token failed)',
            'background: #ff6b6b; color: white; font-weight: bold; padding: 4px 8px;',
            '\n📍 Error:', refreshError,
            '\n💡 Set VITE_DISABLE_AUTO_LOGOUT=false to enable auto-logout'
          );
          return Promise.reject(refreshError);
        }

        localStorage.removeItem('token-storage');
        window.location.href = '/logout';
        return Promise.reject(refreshError);
      } finally {
        // ⚠️ CRITICAL: refresh 완료 플래그 해제
        isRefreshing = false;
        globalRefreshPromise = null;
        console.log('[Refresh] 🏁 Refresh flow completed, queue processed');
      }
    }

    // 401이 아닌 다른 에러는 로그만 남기고 reject
    if (error.response?.status !== 401) {
      console.error('[Axios] Request failed:', {
        url: error.config?.url,
        method: error.config?.method,
        status: error.response?.status,
        statusText: error.response?.statusText,
        data: error.response?.data,
      });
    }

    return Promise.reject(error);
  }
);

export default apiClient;
